import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { playChime, alertSoundEnabled } from "@/lib/chime";

const SimContext = createContext(null);
const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

export function SimProvider({ children }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wsStatus, setWsStatus] = useState("connecting"); // connecting|connected|reconnecting|lost
  const [eventTick, setEventTick] = useState(0);
  const [alertActive, setAlertActive] = useState(false);

  const timer = useRef(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const attemptsRef = useRef(0);
  const lastPendingRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/sim/state");
      setState(data);
    } catch (e) {
      /* transient */
    } finally {
      setLoading(false);
    }
  }, []);

  // Slow fallback poll (WebSocket carries real-time events)
  useEffect(() => {
    refresh();
    timer.current = setInterval(refresh, 6000);
    return () => clearInterval(timer.current);
  }, [refresh]);

  // WebSocket real-time channel with automatic reconnection
  useEffect(() => {
    let closed = false;
    const connect = () => {
      if (closed) return;
      let ws;
      try {
        ws = new WebSocket(`${WS_BASE}/api/ws`);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        if (closed) return;
        attemptsRef.current = 0;
        setWsStatus("connected");
      };
      ws.onmessage = () => {
        setEventTick((t) => t + 1);
        refresh();
      };
      ws.onclose = () => {
        if (closed) return;
        scheduleReconnect();
      };
      ws.onerror = () => {
        try { ws.close(); } catch {}
      };
    };
    const scheduleReconnect = () => {
      if (closed) return;
      attemptsRef.current += 1;
      setWsStatus(attemptsRef.current > 5 ? "lost" : "reconnecting");
      const delay = Math.min(1000 * attemptsRef.current, 8000);
      clearTimeout(reconnectRef.current);
      reconnectRef.current = setTimeout(connect, delay);
    };
    connect();
    const ping = setInterval(() => {
      try {
        if (wsRef.current && wsRef.current.readyState === 1) wsRef.current.send("ping");
      } catch {}
    }, 25000);
    return () => {
      closed = true;
      clearInterval(ping);
      clearTimeout(reconnectRef.current);
      try { wsRef.current && wsRef.current.close(); } catch {}
    };
  }, [refresh]);

  // Detect a NEW pending decision -> visual attention + one soft chime
  useEffect(() => {
    const pd = state?.pending_decision;
    const cur = pd?.event_no || null;
    if (cur && cur !== lastPendingRef.current) {
      lastPendingRef.current = cur;
      setAlertActive(true);
      if (alertSoundEnabled()) playChime(pd.kind === "safety_event" ? "safety" : "decision");
    }
    if (!cur) {
      lastPendingRef.current = null;
      setAlertActive(false);
    }
  }, [state?.pending_decision?.event_no, state?.pending_decision?.kind]);

  const acknowledge = useCallback(() => setAlertActive(false), []);

  const trigger = useCallback(async (scenario) => {
    await api.post(`/sim/trigger/${scenario}`);
    await refresh();
  }, [refresh]);

  const decide = useCallback(async (payload) => {
    const { data } = await api.post("/sim/decision", payload);
    await refresh();
    return data;
  }, [refresh]);

  return (
    <SimContext.Provider value={{
      state, loading, wsStatus, eventTick, alertActive, acknowledge,
      refresh, trigger, decide,
    }}>
      {children}
    </SimContext.Provider>
  );
}

export const useSim = () => useContext(SimContext);
